/**
 * Página CrmDeals - Listagem Premium de Deals/Negócios
 * 
 * Características:
 * - Header compacto com métricas inline
 * - Tabela premium com novas colunas (probabilidade, idade)
 * - Paginação server-side via RPC get_deals_paginated
 * - Integração com modal premium de detalhes
 * - Tipografia compacta e design moderno
 * 
 * Versão: 2.1.0 - Adicionada paginação server-side
 *
 * RELEASE: 2026-10-22 v8.3.0 - Busca do cabeçalho centralizada. O bloco do título ganhou peso
 *          mínimo relativo (`sm:min-w-[min(24rem,34%,calc(100%_-_35.75rem))]`) e o campo passou
 *          a `flex-1 max-w-md min-w-0`: antes, com `justify-between`, a barra de ações (~380px)
 *          muito mais larga que o título empurrava a busca para junto dele (centro 331px num
 *          cabeçalho de 940px). Agora o campo fica no meio (centro 435px) e reduz de tamanho em
 *          janelas estreitas — nunca abaixo de ~128px — sem quebrar os botões para a 2ª linha.
 */
import React, { useState, useMemo, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { 
  Plus, 
  Upload,
  Search, 
  Filter, 
  Eye, 
  Pencil, 
  Trophy, 
  XCircle,
  RotateCcw,
  Briefcase,
  X,
  ArrowUpDown,
  ChevronUp,
  ChevronDown,
  RefreshCw,
  Clock,
  CalendarDays,
  SlidersHorizontal,
  Building2,
  Layers,
  CircleDot,
  Flag,
  ArrowRightLeft,
  ListChecks
} from 'lucide-react';

import { useRefreshWithTimestamp } from '@/hooks/useRefreshWithTimestamp';
import { useAuth } from '@/hooks/useAuth';
import { useCrmDeals, CrmDeal, DealStatus, DealPriority } from '@/hooks/useCrmDeals';
import { useCrmDealsPaginated, PaginatedDeal, DealSortField, SortDirection } from '@/hooks/useCrmDealsPaginated';
import { useDealListKpis } from '@/hooks/useDealListKpis';
// Exportação genérica de dados (respeita os filtros aplicados na tela)
import { ExportMenuButton } from '@/components/export/ExportMenuButton';
import type { CrmDealsExportParams } from '@/lib/export/sources/crmDealsSource';
import { DealListKpiCards } from '@/components/crm/deals/DealListKpiCards';
import { CrmSellerFilter, CrmDealPhaseFilter, CrmDealTagsFilter, CrmDealSourceFilter, CrmUserFilter, CrmDealCreationOriginFilter, CrmLossReasonFilter, type CreationOriginFilterValue } from '@/components/crm/filters';
import { DealOriginBadge } from '@/components/crm/deals/components/DealOriginBadge';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useCrmWorkspacesHierarchy } from '@/hooks/useCrmWorkspacesHierarchy';
import { DealFormDialog } from '@/components/crm/deals/DealFormDialog';
import { DealDetailsModalPremium } from '@/components/crm/deals/modal/DealDetailsModalPremium';
import { DealStatusBadge } from '@/components/crm/deals/DealStatusBadge';
import { DealPhaseProgress } from '@/components/crm/deals/DealPhaseProgress';
import { DealAgeIndicator } from '@/components/crm/deals/DealAgeIndicator';
import { LeadSourceBadge } from '@/components/crm/deals/components/LeadSourceBadge';
import { DealTagBadge } from '@/components/crm/deals/tags/DealTagBadge';
import { formatDealCode } from '@/lib/formatters';
// DealQuickStats removido - substituído por DealListKpiCards (v3.1.0)
import { DealPriorityIndicator } from '@/components/crm/deals/DealPriorityIndicator';
import { PriorityFlag } from '@/components/tasks/PriorityFlag';
import { usePipelinePhaseStats } from '@/hooks/usePipelinePhaseStats';
import { PaginationFooter } from '@/components/ui/pagination-footer';
import { cn } from '@/lib/utils';
import { PriceDisplay } from '@/components/crm/PriceDisplay';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { useAutoOpenFromQuery } from '@/hooks/useAutoOpenFromQuery';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ScrollIndicators } from '@/components/ui/scroll-indicators';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import { BulkTransferDialog } from '@/components/tenant/transfers/BulkTransferDialog';
import { DealBulkEditBar } from '@/components/crm/deals/DealBulkEditBar';
import { Checkbox } from '@/components/ui/checkbox';
import { usePermissions } from '@/hooks/usePermissions';
import { useCrmPhasePermissions } from '@/hooks/useCrmPhasePermissions';
import { Link } from 'react-router-dom';
// v8.0.0 (2026-09-07): tabela extraída + visão agrupada (paridade com Tarefas)
import {
  DealsListTable,
  DealsListTableProvider,
  type DealsListTableContextValue,
} from '@/components/crm/deals/list/DealsListTable';
import { DealGroupedView, type DealGroupBy } from '@/components/crm/deals/list/DealGroupedView';
import { useDealsListViewPreference } from '@/hooks/useDealsListViewPreference';
import { LayoutList, Rows3, ChevronsUpDown, MoreHorizontal } from 'lucide-react';


/**
 * Componente auxiliar para linha de filtro de data (De/Até) dentro do Popover "Mais"
 * Cada linha representa um filtro de data com dois date pickers e botão limpar
 */
interface DateFilterRowProps {
  label: string;
  fromDate: Date | undefined;
  toDate: Date | undefined;
  onFromChange: (date: Date | undefined) => void;
  onToChange: (date: Date | undefined) => void;
}

function DateFilterRow({ label, fromDate, toDate, onFromChange, onToChange }: DateFilterRowProps) {
  return (
    <div className="space-y-1.5">
      {/* Label com ícone pequeno e cor foreground (padrão Kanban) */}
      <Label className="text-xs flex items-center gap-1.5">
        <CalendarDays className="h-3 w-3" />
        {label}
      </Label>
      <div className="flex gap-2">
        {/* Date picker "De" */}
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="h-7 flex-1 justify-start text-xs font-normal">
              {fromDate ? format(fromDate, 'dd/MM/yy') : 'De'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start" side="bottom">
            <Calendar
              mode="single"
              selected={fromDate}
              onSelect={onFromChange}
              className="p-3 pointer-events-auto"
              locale={ptBR}
            />
          </PopoverContent>
        </Popover>
        {/* Date picker "Até" */}
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="h-7 flex-1 justify-start text-xs font-normal">
              {toDate ? format(toDate, 'dd/MM/yy') : 'Até'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start" side="bottom">
            <Calendar
              mode="single"
              selected={toDate}
              onSelect={onToChange}
              className="p-3 pointer-events-auto"
              locale={ptBR}
            />
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}

/**
 * Props opcionais — quando renderizada dentro de um Dialog (DealsListModal)
 * a partir de KPIs do dashboard CRM (v2.2.0 - drill-down).
 *
 * - `embedded`: oculta o bloco de título "Negócios / Gerencie..." (o modal
 *   já fornece o seu próprio header). Mantém toda a barra de busca, filtros,
 *   KPIs, tabela, bulk-edit, paginação e modais internos.
 * - `presetFilters`: valores iniciais dos filtros (status, workspace, seller,
 *   pipeline, fase e datas). Usados como estado inicial dos `useState` — o
 *   usuário pode ajustá-los dentro do modal se desejar.
 * - `hideNewDealButton`: oculta o botão "Novo Negócio" no header da página
 *   (recomendado em modo modal para evitar criação fora de contexto).
 */
export interface CrmDealsProps {
  embedded?: boolean;
  hideNewDealButton?: boolean;
  presetFilters?: {
    workspace_id?: string;
    pipeline_id?: string;
    phase_id?: string;
    status?: DealStatus;
    priority?: DealPriority;
    seller_id?: string;
    assigned_to?: string;
    created_from?: Date;
    created_to?: Date;
    won_date_from?: Date;
    won_date_to?: Date;
    lost_date_from?: Date;
    lost_date_to?: Date;
    /**
     * v9.0.0 (2026-09-17) — Recortes de auditoria vindos dos indicadores das
     * abas "Por Recorrência" e "Top 10 Clientes". São filtros fixos do recorte
     * (não editáveis na barra de filtros) aplicados diretamente na consulta.
     */
    product_ids?: string[];
    item_unit_types?: string[];
    recurrence_intervals?: string[];
    term_types?: string[];
    exclude_unit_types?: string[];
    customer_ids?: string[];
    /** Restringe a listagem aos negócios com ao menos um item descontado. */
    has_discount?: boolean;
    /** Recorte de detalhamento por uma ou mais tags de negócio. */
    tag_ids?: string[];
    /** Restringe a listagem a negócios sem nenhuma tag. */
    tags_is_empty?: boolean;
  };
}

/**
 * Página premium de listagem de Deals com paginação server-side
 */
export default function CrmDeals({
  embedded = false,
  hideNewDealButton = false,
  presetFilters,
}: CrmDealsProps = {}) {
  const queryClient = useQueryClient();
  const { currentTenant } = useAuth();
  const { isAdmin, isSupervisor, canCreate, canEdit } = usePermissions();
  
  // Permissão para transferência em massa: Admin ou Supervisor
  const canBulkTransfer = isAdmin || isSupervisor;
  const canBulkEdit = isAdmin || isSupervisor;
  const [bulkTransferOpen, setBulkTransferOpen] = useState(false);
  
  // Estado de edição em massa (v4.0.0)
  const [bulkEditMode, setBulkEditMode] = useState(false);
  const [selectedDealIds, setSelectedDealIds] = useState<Set<string>>(new Set());
  
  // Hook para refresh com timestamp (v2.3.0)
  const { isRefreshing, formattedTime, triggerRefresh } = useRefreshWithTimestamp();
  
  // Estados de filtro — inicializados a partir de presetFilters (drill-down KPI)
  const [search, setSearch] = useState('');
  const [workspaceFilter, setWorkspaceFilter] = useState<string>(presetFilters?.workspace_id ?? 'all');
  const [statusFilter, setStatusFilter] = useState<DealStatus | 'all'>(presetFilters?.status ?? 'all');
  const [pipelineFilter, setPipelineFilter] = useState<string>(presetFilters?.pipeline_id ?? 'all');
  const [priorityFilter, setPriorityFilter] = useState<DealPriority | 'all'>(presetFilters?.priority ?? 'all');
  const [sellerFilter, setSellerFilter] = useState<string | null>(presetFilters?.seller_id ?? null);
  // v5.0.0 (2026-04-23): Novos filtros — Fase, Tags, Origem (inline) + Responsável e Data Previsão (Sheet)
  const [phaseFilter, setPhaseFilter] = useState<string | null>(presetFilters?.phase_id ?? null);
  // v10.0.0 (2026-10-06): permite abrir a listagem oficial já filtrada por tags.
  const [tagsFilter, setTagsFilter] = useState<string[]>(presetFilters?.tag_ids ?? []);
  // v2.0.0 (2026-07-11): Origem passou a ser multi-seleção
  const [sourceFilter, setSourceFilter] = useState<string[]>([]);
  const [assignedToFilter, setAssignedToFilter] = useState<string | null>(presetFilters?.assigned_to ?? null);
  const [expectedCloseFrom, setExpectedCloseFrom] = useState<Date | undefined>();
  const [expectedCloseTo, setExpectedCloseTo] = useState<Date | undefined>();
  // v6.0.0 (2026-05-16): Origem do Cadastro (manual/import/webhook/scheduling/flow/api) + lista importação
  const [creationOriginFilter, setCreationOriginFilter] = useState<CreationOriginFilterValue>({
    types: [],
    importJobId: null,
  });
  // v7.0.0 (2026-07-11): Motivos de Perda (multi-seleção)
  const [lossReasonIds, setLossReasonIds] = useState<string[]>([]);
  
  // v3.0.0: Estados de filtros de data avançados (botão "Mais") — inicializados via presetFilters
  const [isMoreFiltersOpen, setIsMoreFiltersOpen] = useState(false);
  const [createdFrom, setCreatedFrom] = useState<Date | undefined>(presetFilters?.created_from);
  const [createdTo, setCreatedTo] = useState<Date | undefined>(presetFilters?.created_to);
  const [wonDateFrom, setWonDateFrom] = useState<Date | undefined>(presetFilters?.won_date_from);
  const [wonDateTo, setWonDateTo] = useState<Date | undefined>(presetFilters?.won_date_to);
  const [lostDateFrom, setLostDateFrom] = useState<Date | undefined>(presetFilters?.lost_date_from);
  const [lostDateTo, setLostDateTo] = useState<Date | undefined>(presetFilters?.lost_date_to);
  const [phaseUpdatedFrom, setPhaseUpdatedFrom] = useState<Date | undefined>();
  const [phaseUpdatedTo, setPhaseUpdatedTo] = useState<Date | undefined>();
  // Estados de paginação (v2.1.0)
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  
  // Estados de ordenação (v2.2.0)
  const [sortField, setSortField] = useState<DealSortField>('created_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  
  // Estados de formulário
  const [formOpen, setFormOpen] = useState(false);
  // Auto-abre modal quando vindo do botão 'Novo' do header (Quick Create)
  useAutoOpenFromQuery('1', () => setFormOpen(true));
  const [editingDeal, setEditingDeal] = useState<CrmDeal | null>(null);
  
  // Estados do modal de detalhes
  const [viewDealId, setViewDealId] = useState<string | null>(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);

  // v3.5.0: o modal de sucesso pós-criação passou a ser montado pelo DealFormDialog

  // v8.0.0 (2026-09-07): visualização lista x agrupada (preferência persistida)
  const { viewMode, setViewMode, groupBy, setGroupBy, groupsDefault, setGroupsDefault } =
    useDealsListViewPreference();
  // Sinal para expandir/colapsar todos os grupos de uma vez
  const [expandAllSignal, setExpandAllSignal] = useState(0);




  // ====== Permissões granulares por fase (escopadas ao workspace filtrado) ======
  // Quando o usuário tem um workspace selecionado no filtro, o hook resolve a matriz
  // [perfil × fase × ação]. Sem workspace selecionado, canDoOnPhase retorna true
  // (granular OFF) e caímos para a regra geral via canEdit('crm_deals').
  const phasePermsWorkspaceId = workspaceFilter !== 'all' ? workspaceFilter : null;
  const { canDoOnPhase, isGranularEnabled } = useCrmPhasePermissions(phasePermsWorkspaceId);

  /**
   * Calcula permissões da linha (ações do dropdown) seguindo a mesma matriz
   * usada em DealCard/DealKanbanBoard e DealModalHeader/DealDetailsModalPremium.
   */
  const computeRowPerms = (deal: PaginatedDeal) => {
    if (isAdmin) {
      return { canEditRow: true, canChangePhaseRow: true, canReopenRow: true };
    }
    if (!isGranularEnabled) {
      // Granular OFF (ou workspace não filtrado) → cai na permissão geral do menu
      const general = canEdit('crm_deals');
      return { canEditRow: general, canChangePhaseRow: general, canReopenRow: general };
    }
    return {
      canEditRow: canDoOnPhase(deal.phase_id, 'can_edit'),
      canChangePhaseRow: canDoOnPhase(deal.phase_id, 'can_change_phase'),
      canReopenRow: canDoOnPhase(deal.phase_id, 'can_reopen'),
    };
  };


  // Busca hierarquia de workspaces e pipelines
  const { workspaces } = useCrmWorkspacesHierarchy();

  // Pipelines filtrados pelo workspace selecionado
  const filteredPipelines = useMemo(() => {
    if (workspaceFilter === 'all') {
      return workspaces.flatMap(w => w.pipelines);
    }
    const selectedWorkspace = workspaces.find(w => w.id === workspaceFilter);
    return selectedWorkspace?.pipelines || [];
  }, [workspaces, workspaceFilter]);

  // Reset de página ao mudar filtros (v2.1.0 + v3.0.0 datas + v5.0.0 novos filtros)
  useEffect(() => {
    setCurrentPage(1);
  }, [search, workspaceFilter, statusFilter, pipelineFilter, priorityFilter, sellerFilter, phaseFilter, tagsFilter, sourceFilter, assignedToFilter, expectedCloseFrom, expectedCloseTo, createdFrom, createdTo, wonDateFrom, wonDateTo, lostDateFrom, lostDateTo, phaseUpdatedFrom, phaseUpdatedTo, lossReasonIds]);

  // Quando workspace mudar, resetar pipeline e fase para 'all'
  const handleWorkspaceChange = (value: string) => {
    setWorkspaceFilter(value);
    setPipelineFilter('all');
    setPhaseFilter(null);
  };

  // Quando pipeline mudar, resetar fase
  const handlePipelineChange = (value: string) => {
    setPipelineFilter(value);
    setPhaseFilter(null);
  };

  // Handler para ordenação por colunas (v2.2.0)
  const handleSort = (field: DealSortField) => {
    if (sortField === field) {
      // Alterna direção se clicar no mesmo campo
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      // Novo campo, começa descendente
      setSortField(field);
      setSortDirection('desc');
    }
    setCurrentPage(1); // Reset para primeira página
  };

  // Filtros aplicados na tela — extraídos para constante para que a
  // exportação de dados use EXATAMENTE os mesmos filtros da listagem.
  const dealFilters = {
      workspace_id: workspaceFilter !== 'all' ? workspaceFilter : undefined,
      pipeline_id: pipelineFilter !== 'all' ? pipelineFilter : undefined,
      phase_id: phaseFilter || undefined,
      status: statusFilter !== 'all' ? statusFilter : undefined,
      priority: priorityFilter !== 'all' ? priorityFilter : undefined,
      seller_id: sellerFilter || undefined,
      assigned_to: assignedToFilter || undefined,
      source_ids: sourceFilter.length > 0 ? sourceFilter : undefined,
      tag_ids: tagsFilter.length > 0 ? tagsFilter : undefined,
      tags_is_empty: presetFilters?.tags_is_empty,
      search: search || undefined,
      // v3.0.0: Filtros de data avançados
      created_from: createdFrom ? format(createdFrom, 'yyyy-MM-dd') : undefined,
      created_to: createdTo ? format(createdTo, 'yyyy-MM-dd') : undefined,
      won_date_from: wonDateFrom ? format(wonDateFrom, 'yyyy-MM-dd') : undefined,
      won_date_to: wonDateTo ? format(wonDateTo, 'yyyy-MM-dd') : undefined,
      lost_date_from: lostDateFrom ? format(lostDateFrom, 'yyyy-MM-dd') : undefined,
      lost_date_to: lostDateTo ? format(lostDateTo, 'yyyy-MM-dd') : undefined,
      phase_updated_from: phaseUpdatedFrom ? format(phaseUpdatedFrom, 'yyyy-MM-dd') : undefined,
      phase_updated_to: phaseUpdatedTo ? format(phaseUpdatedTo, 'yyyy-MM-dd') : undefined,
      // v5.0.0: Data Previsão
      expected_close_from: expectedCloseFrom ? format(expectedCloseFrom, 'yyyy-MM-dd') : undefined,
      expected_close_to: expectedCloseTo ? format(expectedCloseTo, 'yyyy-MM-dd') : undefined,
      // v6.0.0 (2026-05-16): Origem do Cadastro
      creation_origin_types: creationOriginFilter.types.length > 0 ? creationOriginFilter.types : undefined,
      creation_origin_source_id: creationOriginFilter.importJobId || undefined,
      // v7.0.0 (2026-07-11): Motivos de Perda
      loss_reason_ids: lossReasonIds.length > 0 ? lossReasonIds : undefined,
      // v9.0.0 (2026-09-17): recortes fixos de auditoria (produto/recorrência/cliente)
      product_ids: presetFilters?.product_ids,
      item_unit_types: presetFilters?.item_unit_types,
      recurrence_intervals: presetFilters?.recurrence_intervals,
      term_types: presetFilters?.term_types,
      exclude_unit_types: presetFilters?.exclude_unit_types,
      customer_ids: presetFilters?.customer_ids,
      has_discount: presetFilters?.has_discount,
  };

  // Busca deals com paginação e ordenação server-side (v2.2.0)
  const { 
    deals, 
    totalCount,
    isLoading,
    isFetching,
    refetch,
  } = useCrmDealsPaginated(
    dealFilters,
    { 
      page: currentPage, 
      pageSize,
      sortField,
      sortDirection
    }
  );

  // Parâmetros da exportação de dados da listagem (mesmos filtros/ordenação)
  const exportParams: CrmDealsExportParams = {
    kind: 'list',
    filters: dealFilters,
    sortField,
    sortDirection,
  };

  // Mutations continuam vindo do useCrmDeals (com query disabled)
  const { markAsWon, markAsLost, reopenDeal } = useCrmDeals(undefined, { enabled: false });

  // v3.2.0: Estado independente de filtro KPI (padrão TaskList)
  const [kpiFilter, setKpiFilter] = useState<string | null>(null);

  // v3.1.0: KPIs server-side via RPC (substitui stats client-side e DealQuickStats)
  // v3.3.0: Filtros de KPI separados para evitar circularidade — exclui filtros aplicados pelo card KPI
  const dealKpiFilters = useMemo(() => ({
    workspace_id: workspaceFilter !== 'all' ? workspaceFilter : undefined,
    pipeline_id: pipelineFilter !== 'all' ? pipelineFilter : undefined,
    phase_id: phaseFilter || undefined,
    // Quando KPI ativo é um status, não passa status para KPIs (evita circular)
    status: (kpiFilter && kpiFilter !== 'created_today') ? undefined : (statusFilter !== 'all' ? statusFilter : undefined),
    priority: priorityFilter !== 'all' ? priorityFilter : undefined,
    seller_id: sellerFilter || undefined,
    assigned_to: assignedToFilter || undefined,
    source_ids: sourceFilter.length > 0 ? sourceFilter : undefined,
    tag_ids: tagsFilter.length > 0 ? tagsFilter : undefined,
    search: search || undefined,
    // Quando KPI ativo é "created_today", não passa created_from/to para KPIs (evita circular)
    created_from: kpiFilter === 'created_today' ? undefined : (createdFrom ? format(createdFrom, 'yyyy-MM-dd') : undefined),
    created_to: kpiFilter === 'created_today' ? undefined : (createdTo ? format(createdTo, 'yyyy-MM-dd') : undefined),
    won_date_from: wonDateFrom ? format(wonDateFrom, 'yyyy-MM-dd') : undefined,
    won_date_to: wonDateTo ? format(wonDateTo, 'yyyy-MM-dd') : undefined,
    lost_date_from: lostDateFrom ? format(lostDateFrom, 'yyyy-MM-dd') : undefined,
    lost_date_to: lostDateTo ? format(lostDateTo, 'yyyy-MM-dd') : undefined,
    phase_updated_from: phaseUpdatedFrom ? format(phaseUpdatedFrom, 'yyyy-MM-dd') : undefined,
    phase_updated_to: phaseUpdatedTo ? format(phaseUpdatedTo, 'yyyy-MM-dd') : undefined,
    // v5.0.0: Data Previsão
    expected_close_from: expectedCloseFrom ? format(expectedCloseFrom, 'yyyy-MM-dd') : undefined,
    expected_close_to: expectedCloseTo ? format(expectedCloseTo, 'yyyy-MM-dd') : undefined,
    // v3.4.0: Filtro Origem do Cadastro (paridade com a listagem)
    creation_origin_types: creationOriginFilter.types.length > 0 ? creationOriginFilter.types : undefined,
    creation_origin_source_id: creationOriginFilter.importJobId || undefined,
    // v3.5.0 (2026-07-11): Motivos de Perda (paridade com a listagem)
    loss_reason_ids: lossReasonIds.length > 0 ? lossReasonIds : undefined,
  }), [workspaceFilter, pipelineFilter, phaseFilter, statusFilter, priorityFilter, sellerFilter, assignedToFilter, sourceFilter, tagsFilter, search, createdFrom, createdTo, wonDateFrom, wonDateTo, lostDateFrom, lostDateTo, phaseUpdatedFrom, phaseUpdatedTo, expectedCloseFrom, expectedCloseTo, kpiFilter, creationOriginFilter, lossReasonIds]);

  const { kpis: dealKpis, isLoading: kpisLoading, refetch: refetchKpis } = useDealListKpis(dealKpiFilters);

  /**
   * Handler para filtro via KPI cards (toggle)
   * Aplica filtro de data para "created_today" e status para os demais
   */
  const handleKpiFilter = (filter: string | null) => {
    // Toggle: se já está ativo, limpa
    if (kpiFilter === filter) {
      setKpiFilter(null);
      setStatusFilter('all');
      setCreatedFrom(undefined);
      setCreatedTo(undefined);
      return;
    }

    setKpiFilter(filter);

    if (filter === null) {
      setStatusFilter('all');
      setCreatedFrom(undefined);
      setCreatedTo(undefined);
    } else if (filter === 'created_today') {
      // "Criados Hoje": limpa status e aplica filtro de data para hoje (timezone local do navegador)
      // O backend (get_deals_paginated v1.9.0) usa AT TIME ZONE p_timezone para comparar,
      // mantendo paridade com o KPI get_deal_list_kpis v2.4.0.
      setStatusFilter('all');
      const now = new Date();
      const localToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      setCreatedFrom(localToday);
      setCreatedTo(localToday);

    } else if (filter === 'open') {
      setStatusFilter('open' as DealStatus);
      setCreatedFrom(undefined);
      setCreatedTo(undefined);
    } else if (filter === 'won') {
      setStatusFilter('won' as DealStatus);
      setCreatedFrom(undefined);
      setCreatedTo(undefined);
    } else if (filter === 'lost') {
      setStatusFilter('lost' as DealStatus);
      setCreatedFrom(undefined);
      setCreatedTo(undefined);
    }
  };

  // v3.0.0: Contagem de filtros do Sheet "Mais" (datas + responsável + data previsão)
  const activeDateFiltersCount = useMemo(() => {
    let count = 0;
    if (createdFrom || createdTo) count++;
    if (wonDateFrom || wonDateTo) count++;
    if (lostDateFrom || lostDateTo) count++;
    if (phaseUpdatedFrom || phaseUpdatedTo) count++;
    if (expectedCloseFrom || expectedCloseTo) count++;
    if (assignedToFilter) count++;
    // v6.0.0: Filtro de Origem do Cadastro
    if (creationOriginFilter.types.length > 0) count++;
    // v7.0.0 (2026-07-11): Motivos de Perda
    if (lossReasonIds.length > 0) count++;
    return count;
  }, [createdFrom, createdTo, wonDateFrom, wonDateTo, lostDateFrom, lostDateTo, phaseUpdatedFrom, phaseUpdatedTo, expectedCloseFrom, expectedCloseTo, assignedToFilter, creationOriginFilter, lossReasonIds]);

  // Verifica se há filtros ativos (inclui novos filtros v5.0.0)
  const hasActiveFilters = !!(
    search || workspaceFilter !== 'all' || statusFilter !== 'all' || pipelineFilter !== 'all' ||
    priorityFilter !== 'all' || sellerFilter !== null || phaseFilter !== null ||
    tagsFilter.length > 0 || sourceFilter.length > 0 || activeDateFiltersCount > 0
  );

  // Limpa todos os filtros
  const clearFilters = () => {
    setSearch('');
    setWorkspaceFilter('all');
    setStatusFilter('all');
    setPipelineFilter('all');
    setPriorityFilter('all');
    setSellerFilter(null);
    setKpiFilter(null);
    // v5.0.0: Limpar novos filtros (Fase, Tags, Origem, Responsável, Data Previsão)
    setPhaseFilter(null);
    setTagsFilter([]);
    setSourceFilter([]);
    setAssignedToFilter(null);
    setExpectedCloseFrom(undefined);
    setExpectedCloseTo(undefined);
    // v3.0.0: Limpar filtros de data
    setCreatedFrom(undefined);
    setCreatedTo(undefined);
    setWonDateFrom(undefined);
    setWonDateTo(undefined);
    setLostDateFrom(undefined);
    setLostDateTo(undefined);
    setPhaseUpdatedFrom(undefined);
    setPhaseUpdatedTo(undefined);
    // v6.0.0: Limpar Origem do Cadastro
    setCreationOriginFilter({ types: [], importJobId: null });
    // v7.0.0 (2026-07-11): Limpar Motivos de Perda
    setLossReasonIds([]);
  };

  // v4.0.0: Handlers de edição em massa
  const toggleDealSelection = (dealId: string) => {
    setSelectedDealIds(prev => {
      const next = new Set(prev);
      if (next.has(dealId)) next.delete(dealId);
      else next.add(dealId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedDealIds.size === deals.length) {
      setSelectedDealIds(new Set());
    } else {
      setSelectedDealIds(new Set(deals.map(d => d.id)));
    }
  };

  const exitBulkEditMode = () => {
    setBulkEditMode(false);
    setSelectedDealIds(new Set());
  };

  // Deals selecionados para a barra de edição
  const selectedDeals = useMemo(() => 
    deals.filter(d => selectedDealIds.has(d.id)),
    [deals, selectedDealIds]
  );

  // Função para atualizar dados manualmente (v2.3.0)
  const handleRefresh = async () => {
    await triggerRefresh(async () => {
      await Promise.all([refetch(), refetchKpis()]);
    });
  };

  // v2.0.0: Handler para marcar como ganho com validação de produtos/serviços
  const handleMarkAsWon = async (dealId: string) => {
    // Busca contagem de items
    const { count } = await supabase
      .from('crm_deal_items')
      .select('id', { count: 'exact', head: true })
      .eq('deal_id', dealId);
    
    if (!count || count === 0) {
      toast.error('Adicione ao menos um produto/serviço antes de marcar como ganho');
      return;
    }
    
    markAsWon.mutate(dealId);
  };

  // Abre modal de detalhes do deal
  const handleViewDeal = (deal: PaginatedDeal) => {
    setViewDealId(deal.id);
    setViewModalOpen(true);
  };

  // Abre formulário para edição (converte PaginatedDeal para CrmDeal parcial)
  const handleEdit = (deal: PaginatedDeal) => {
    // Cria objeto CrmDeal parcial para o formulário
    const crmDeal: CrmDeal = {
      id: deal.id,
      tenant_id: currentTenant?.id || '',
      pipeline_id: deal.pipeline_id,
      phase_id: deal.phase_id,
      contact_id: deal.contact_id,
      title: deal.title,
      company_name: deal.company_name,
      description: null,
      value: deal.value,
      currency: deal.currency,
      probability: deal.probability,
      expected_close_date: deal.expected_close_date,
      actual_close_date: null,
      status: deal.status,
      priority: deal.priority,
      loss_reason: null,
      assigned_to: deal.assigned_to,
      source: deal.source || null,
      sort_order: deal.sort_order,
      custom_fields: {},
      metadata: {},
      created_by: null,
      created_at: deal.created_at,
      updated_at: deal.created_at,
      // Relacionamentos
      contact: deal.contact_id ? {
        id: deal.contact_id,
        name: deal.contact_name,
        phone: deal.contact_phone,
        email: null,
        avatar_url: deal.contact_avatar,
      } : null,
      phase: {
        id: deal.phase_id,
        name: deal.phase_name || '',
        color: deal.phase_color,
        is_won: deal.phase_is_won,
        is_lost: deal.phase_is_lost,
        sort_order: deal.phase_sort_order || 0,
      },
      pipeline: {
        id: deal.pipeline_id,
        name: deal.pipeline_name || '',
      },
      assigned_user: deal.assigned_to ? {
        id: deal.assigned_to,
        full_name: deal.assigned_user_name,
        avatar_url: deal.assigned_user_avatar,
      } : null,
      seller: deal.seller_id ? {
        id: deal.seller_id,
        name: deal.seller_name || '',
        email: null,
        phone: null,
        avatar_url: deal.seller_avatar,  // v1.0.2 - Avatar do vendedor
      } : null,
    };
    setEditingDeal(crmDeal);
    setFormOpen(true);
  };

  // Fecha formulário
  const handleCloseForm = () => {
    setEditingDeal(null);
    setFormOpen(false);
  };

  // Handler para mudança de página
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Handler para mudança de itens por página
  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  /**
   * v8.0.0 (2026-09-07): Contexto compartilhado pela tabela de negócios.
   * A mesma tabela é usada pela listagem normal e por cada grupo da visão
   * "Agrupar por" — nenhuma regra de permissão/ação é duplicada.
   */
  const listTableCtx: DealsListTableContextValue = useMemo(
    () => ({
      sortField,
      sortDirection,
      onSort: handleSort,
      bulkEditMode,
      selectedDealIds,
      onToggleDealSelection: toggleDealSelection,
      onToggleSelectAll: (rows) => {
        const allSelected = rows.length > 0 && rows.every((d) => selectedDealIds.has(d.id));
        setSelectedDealIds((prev) => {
          const next = new Set(prev);
          rows.forEach((d) => (allSelected ? next.delete(d.id) : next.add(d.id)));
          return next;
        });
      },
      onViewDeal: handleViewDeal,
      onEditDeal: handleEdit,
      computeRowPerms,
      onMarkAsWon: handleMarkAsWon,
      onMarkAsLost: (dealId: string) => markAsLost.mutate({ dealId }),
      onReopen: (dealId: string) => reopenDeal.mutate(dealId),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sortField, sortDirection, bulkEditMode, selectedDealIds, computeRowPerms]
  );

  /**
   * v8.1.0 (2026-09-07): Menu único "Opções de visualização".
   * Reúne, na mesma linha de Importar/Exportar, a alternância Lista x Agrupada,
   * o campo de agrupamento e o expandir/colapsar todos os grupos — antes soltos
   * na barra de filtros. Usa o mesmo estado de useDealsListViewPreference.
   */
  const viewOptionsMenu = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="icon-sm" title="Opções de visualização">
          <MoreHorizontal className="h-3.5 w-3.5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="text-xs">Visualização</DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={viewMode}
          onValueChange={(v) => setViewMode(v as 'list' | 'grouped')}
        >
          <DropdownMenuRadioItem value="list" className="text-xs">
            <LayoutList className="h-3.5 w-3.5 mr-2" />
            Lista
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="grouped" className="text-xs">
            <Rows3 className="h-3.5 w-3.5 mr-2" />
            Agrupada
          </DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>

        <DropdownMenuSeparator />

        {/* Campo de agrupamento — só faz sentido na visão agrupada */}
        <DropdownMenuSub>
          <DropdownMenuSubTrigger disabled={viewMode !== 'grouped'} className="text-xs">
            Agrupar por
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuRadioGroup
              value={groupBy}
              onValueChange={(v) => setGroupBy(v as DealGroupBy)}
            >
              <DropdownMenuRadioItem value="status" className="text-xs">Status</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="seller_id" className="text-xs">Vendedor</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="workspace_id" className="text-xs">Workspace</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="source" className="text-xs">Origem</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="creation_origin" className="text-xs">Cadastro</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="probability" className="text-xs">Probabilidade</DropdownMenuRadioItem>
              {/* v8.2.0: agrupamento de funil agora tem 3 níveis (Workspace → Pipeline → Fase) */}
              <DropdownMenuRadioItem value="pipeline_id" className="text-xs">Workspace, Pipeline e Fase</DropdownMenuRadioItem>

            </DropdownMenuRadioGroup>
          </DropdownMenuSubContent>
        </DropdownMenuSub>

        {/* Expandir / colapsar todos os grupos */}
        <DropdownMenuItem
          disabled={viewMode !== 'grouped'}
          className="text-xs"
          onSelect={() => {
            const next = groupsDefault === 'expanded' ? 'collapsed' : 'expanded';
            setGroupsDefault(next);
            setExpandAllSignal((n) => n + 1);
          }}
        >
          <ChevronsUpDown className="h-3.5 w-3.5 mr-2" />
          {groupsDefault === 'expanded' ? 'Colapsar todos os grupos' : 'Expandir todos os grupos'}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (


    <div className="flex flex-col h-full">
      {/* Header Compacto Premium — oculto em modo embedded (modal já fornece header) */}
      {!embedded && (
        <div className="flex flex-col gap-3 p-4 border-b sm:flex-row sm:items-center sm:justify-between">
          {/* Bloco título — no mobile abriga as ações alinhadas à direita na mesma linha */}
          <div className="flex items-center justify-between gap-2 sm:block sm:min-w-[min(24rem,34%,calc(100%_-_35.75rem))]">
            <div>
              <h1 className="text-xl font-semibold">Negócios</h1>
            </div>
            {/* Ações (mobile) — mesma linha do título, alinhadas à direita */}
            <div className="flex items-center gap-2 sm:hidden">
              {canBulkEdit && (
                <Button
                  variant={bulkEditMode ? "default" : "outline"}
                  size="icon-sm"
                  onClick={() => bulkEditMode ? exitBulkEditMode() : setBulkEditMode(true)}
                  title={bulkEditMode ? "Sair do modo edição em massa" : "Edição em Massa"}
                >
                  <ListChecks className="h-3.5 w-3.5" />
                </Button>
              )}
              {canBulkTransfer && (
                <Button
                  variant="outline"
                  size="icon-sm"
                  onClick={() => setBulkTransferOpen(true)}
                  title="Transferência em Massa"
                >
                  <ArrowRightLeft className="h-3.5 w-3.5" />
                </Button>
              )}
              <Button
                variant="outline"
                size="icon-sm"
                onClick={handleRefresh}
                disabled={isRefreshing || isFetching}
                title="Atualizar dados"
              >
                <RefreshCw className={cn("h-3.5 w-3.5", (isRefreshing || isFetching) && "animate-spin")} />
              </Button>
              {(isAdmin || canCreate('crm.deals.import')) && (
                <Button
                  asChild
                  variant="outline"
                  size="icon-sm"
                  title="Importar Negócios em Massa (CSV)"
                >
                  <Link to="/tenant/lists?tab=deals">
                    <Upload className="h-3.5 w-3.5" />
                  </Link>
                </Button>
              )}
              {/* Exportar dados (mobile) — respeita os filtros da tela */}
              <ExportMenuButton sourceId="crm_deals" params={exportParams} variant="icon" />
              {/* v8.1.0: opções de visualização (lista/agrupada) na mesma linha */}
              {viewOptionsMenu}

              {!hideNewDealButton && (
                <Button 
                  size="xs"
                  onClick={() => setFormOpen(true)}
                  className="hover:shadow-md hover:scale-[1.02] transition-all duration-200"
                >
                  <Plus className="sm:mr-1.5" />
                  <span className="hidden sm:inline">Novo Negócio</span>
                </Button>
              )}
            </div>
          </div>
          {/* Centro: Input de busca premium (desktop).
              v8.3.0: o campo mantém `flex-1 max-w-md` (mesmo tamanho da busca da listagem de
              Tarefas) e ganhou `min-w-0` para poder reduzir sem forçar quebra de linha.
              A centralização vem do peso mínimo relativo dado ao bloco do título
              (`sm:min-w-[min(24rem,34%,calc(100%_-_35.75rem))]`), que equilibra a barra de
              ações à direita; o terceiro termo garante que a busca nunca fique abaixo de
              ~128px (em janelas muito estreitas ela volta a encostar no título). */}
          <div className="relative mx-4 hidden max-w-md flex-1 min-w-0 md:block">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/60" />
            <Input
              placeholder="Buscar por título, empresa ou número..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-10 w-full pl-10 pr-4 text-sm rounded-xl border-none bg-muted/40 hover:bg-muted/60 transition-all duration-200 focus-visible:bg-background focus-visible:shadow-md focus-visible:ring-1 focus-visible:ring-primary/20 focus-visible:ring-offset-0 placeholder:text-muted-foreground/50"
            />
          </div>
          {/* Botões de utilidade + ação principal (desktop) */}
          <div className="hidden sm:flex items-center gap-2">
            {/* Edição em Massa */}
            {canBulkEdit && (
              <Button
                variant={bulkEditMode ? "default" : "outline"}
                size="icon-sm"
                onClick={() => bulkEditMode ? exitBulkEditMode() : setBulkEditMode(true)}
                title={bulkEditMode ? "Sair do modo edição em massa" : "Edição em Massa"}
              >
                <ListChecks className="h-3.5 w-3.5" />
              </Button>
            )}
            {/* Transferência em Massa */}
            {canBulkTransfer && (
              <Button
                variant="outline"
                size="icon-sm"
                onClick={() => setBulkTransferOpen(true)}
                title="Transferência em Massa"
              >
                <ArrowRightLeft className="h-3.5 w-3.5" />
              </Button>
            )}
            {/* Refresh + timestamp */}
            <div className="flex items-center gap-1.5 text-muted-foreground">
              <Button
                variant="outline"
                size="icon-sm"
                onClick={handleRefresh}
                disabled={isRefreshing || isFetching}
                title="Atualizar dados"
              >
                <RefreshCw className={cn("h-3.5 w-3.5", (isRefreshing || isFetching) && "animate-spin")} />
              </Button>
            </div>
            {/* Importar em Massa (CSV) — protegido por permissão granular crm.deals.import */}
            {(isAdmin || canCreate('crm.deals.import')) && (
              <Button
                asChild
                variant="outline"
                size="icon-sm"
                title="Importar Negócios em Massa (CSV)"
              >
                <Link to="/tenant/lists?tab=deals">
                  <Upload className="h-3.5 w-3.5" />
                </Link>
              </Button>
            )}
            {/* Exportar dados (desktop) — mesmo componente/permite arquivo ou e-mail */}
            <ExportMenuButton sourceId="crm_deals" params={exportParams} variant="icon" />
            {/* v8.1.0: opções de visualização (lista/agrupada) na mesma linha */}
            {viewOptionsMenu}

            {!hideNewDealButton && (
              <Button 
                size="xs"
                onClick={() => setFormOpen(true)}
                className="hover:shadow-md hover:scale-[1.02] transition-all duration-200"
              >
                <Plus className="sm:mr-1.5" />
                <span className="hidden sm:inline">Novo Negócio</span>
              </Button>
            )}
          </div>
        </div>
      )}


      {/* v3.2.0: KPI Cards server-side com filtro independente */}
      <DealListKpiCards
        kpis={dealKpis}
        loading={kpisLoading}
        activeStatus={kpiFilter ?? undefined}
        onFilterByStatus={handleKpiFilter}
      />

      {/* v4.0.0: Barra de edição em massa (aparece quando há deals selecionados) */}
      {bulkEditMode && selectedDealIds.size > 0 && (
        <DealBulkEditBar
          selectedDeals={selectedDeals}
          onCancel={exitBulkEditMode}
          onSuccess={exitBulkEditMode}
        />
      )}

      {/* Barra de Filtros Premium */}
      <div className="flex flex-wrap items-center gap-1 p-2 sm:gap-2 sm:p-3 border-b bg-muted/30">
        {/* Busca mobile (visível apenas em telas < md) */}
        <div className="relative flex-1 min-w-[200px] max-w-sm md:hidden">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3 w-3 sm:h-3.5 sm:w-3.5 text-muted-foreground" />
          <Input
            placeholder="Buscar por título, empresa..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-6 text-[11px] sm:h-8 sm:text-sm"
          />
        </div>

        {/* Workspace */}
        <Select value={workspaceFilter} onValueChange={handleWorkspaceChange}>
          <SelectTrigger className="w-[100px] h-6 text-[11px] sm:w-[160px] sm:h-8 sm:text-sm border-0 bg-background rounded-full">
            <div className="flex items-center gap-1.5 min-w-0 flex-1">
              <Building2 className="h-3 w-3 sm:h-3.5 sm:w-3.5 shrink-0 text-muted-foreground" />
              <span className="truncate min-w-0 flex-1 text-left">
                {workspaceFilter === 'all' ? 'Workspace' : workspaces.find(w => w.id === workspaceFilter)?.name || 'Workspace'}
              </span>
            </div>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos workspaces</SelectItem>
            {workspaces.map(w => (
              <SelectItem key={w.id} value={w.id}>
                <span className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: w.color || '#6366f1' }} />
                  {w.name}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Pipeline */}
        <Select value={pipelineFilter} onValueChange={handlePipelineChange}>
          <SelectTrigger className="w-[95px] h-6 text-[11px] sm:w-[150px] sm:h-8 sm:text-sm border-0 bg-background rounded-full">
            <div className="flex items-center gap-1.5 min-w-0 flex-1">
              <Layers className="h-3 w-3 sm:h-3.5 sm:w-3.5 shrink-0 text-muted-foreground" />
              <span className="truncate min-w-0 flex-1 text-left">
                {pipelineFilter === 'all' ? 'Pipeline' : filteredPipelines.find(p => p.id === pipelineFilter)?.name || 'Pipeline'}
              </span>
            </div>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos pipelines</SelectItem>
            {filteredPipelines.map((p, idx) => {
              /** Paleta de cores para pipelines (sem campo color no banco) */
              const pipelineColors = ['#6366f1', '#3b82f6', '#0ea5e9', '#14b8a6', '#22c55e', '#eab308', '#f97316', '#ef4444', '#ec4899', '#8b5cf6'];
              const dotColor = pipelineColors[idx % pipelineColors.length];
              return (
                <SelectItem key={p.id} value={p.id}>
                  <span className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: dotColor }} />
                    {p.name}
                  </span>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>

        {/* v5.0.1 (2026-04-23): Fase movida para logo após Pipeline (depende do pipeline selecionado) */}
        <CrmDealPhaseFilter
          pipelineId={pipelineFilter !== 'all' ? pipelineFilter : null}
          value={phaseFilter}
          onChange={setPhaseFilter}
          className="h-6 text-[11px] px-2 sm:h-8 sm:text-sm sm:px-3"
        />

        {/* Status */}
        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as DealStatus | 'all')}>
          <SelectTrigger className="w-[85px] h-6 text-[11px] sm:w-[130px] sm:h-8 sm:text-sm border-0 bg-background rounded-full">
            <div className="flex items-center gap-1.5 min-w-0 flex-1">
              <CircleDot className="h-3 w-3 sm:h-3.5 sm:w-3.5 shrink-0 text-muted-foreground" />
              <span className="truncate min-w-0 flex-1 text-left">
                {statusFilter === 'all' ? 'Status' : { open: 'Em Aberto', won: 'Ganhos', lost: 'Perdidos' }[statusFilter] || 'Status'}
              </span>
            </div>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos status</SelectItem>
            <SelectItem value="open">
              <span className="flex items-center gap-2">
                <CircleDot className="h-3.5 w-3.5 text-yellow-500" />
                Em Aberto
              </span>
            </SelectItem>
            <SelectItem value="won">
              <span className="flex items-center gap-2">
                <CircleDot className="h-3.5 w-3.5 text-green-500" />
                Ganhos
              </span>
            </SelectItem>
            <SelectItem value="lost">
              <span className="flex items-center gap-2">
                <CircleDot className="h-3.5 w-3.5 text-red-500" />
                Perdidos
              </span>
            </SelectItem>
          </SelectContent>
        </Select>

        {/* Prioridade */}
        <Select value={priorityFilter} onValueChange={(v) => setPriorityFilter(v as DealPriority | 'all')}>
          <SelectTrigger className="w-[95px] h-6 text-[11px] sm:w-[130px] sm:h-8 sm:text-sm border-0 bg-background rounded-full">
            <div className="flex items-center gap-1.5 min-w-0 flex-1">
              <Flag className="h-3 w-3 sm:h-3.5 sm:w-3.5 shrink-0 text-muted-foreground" />
              <span className="truncate min-w-0 flex-1 text-left">
                {priorityFilter === 'all' ? 'Prioridade' : { urgent: 'Urgente', high: 'Alta', medium: 'Média', low: 'Baixa' }[priorityFilter] || 'Prioridade'}
              </span>
            </div>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas</SelectItem>
            {(['urgent', 'high', 'medium', 'low'] as const).map((p) => (
              <SelectItem key={p} value={p}>
                <span className="flex items-center gap-2">
                  <PriorityFlag priority={p} size={13} />
                  {{ urgent: 'Urgente', high: 'Alta', medium: 'Média', low: 'Baixa' }[p]}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Vendedor */}
        <CrmSellerFilter
          value={sellerFilter}
          onChange={setSellerFilter}
          className="h-6 text-[11px] sm:h-8 sm:text-sm"
        />

        {/* v5.0.0: Tags (multi-seleção) */}
        <CrmDealTagsFilter
          value={tagsFilter}
          onChange={setTagsFilter}
          className="h-6 text-[11px] px-2 sm:h-8 sm:text-sm sm:px-3"
        />

        {/* v5.0.0: Origem (single) */}
        <CrmDealSourceFilter
          value={sourceFilter}
          onChange={setSourceFilter}
          className="h-6 text-[11px] px-2 sm:h-8 sm:text-sm sm:px-3"
        />

        {/* v4.0.0: Botão "Mais" com Sheet lateral (padrão FinancialCharges) */}
        <Button
          variant="outline"
          size="sm"
          className="h-6 px-2 text-[10px] sm:h-8 sm:px-3 sm:text-xs gap-1.5 border-0 bg-background rounded-full"
          onClick={() => setIsMoreFiltersOpen(true)}
        >
          <SlidersHorizontal className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
          Mais
          {activeDateFiltersCount > 0 && (
            <Badge variant="secondary" className="h-4 min-w-[16px] px-1 text-[10px] rounded-full bg-primary/15 text-primary border-0">
              {activeDateFiltersCount}
            </Badge>
          )}
        </Button>
        <Sheet open={isMoreFiltersOpen} onOpenChange={setIsMoreFiltersOpen}>
          <SheetContent side="right" className="w-80 p-0 flex flex-col">
            <SheetHeader className="p-4 border-b">
              <div className="flex items-center justify-between">
                <SheetTitle className="text-sm">Filtros Avançados</SheetTitle>
                {activeDateFiltersCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs text-muted-foreground"
                    onClick={() => {
                      setCreatedFrom(undefined); setCreatedTo(undefined);
                      setPhaseUpdatedFrom(undefined); setPhaseUpdatedTo(undefined);
                      setWonDateFrom(undefined); setWonDateTo(undefined);
                      setLostDateFrom(undefined); setLostDateTo(undefined);
                      // v5.0.0: Limpar Responsável e Data Previsão
                      setAssignedToFilter(null);
                      setExpectedCloseFrom(undefined); setExpectedCloseTo(undefined);
                      // v6.0.0: Limpar Origem do Cadastro
                      setCreationOriginFilter({ types: [], importJobId: null });
                      // v7.0.0 (2026-07-11): Limpar Motivos de Perda
                      setLossReasonIds([]);
                    }}
                  >
                    <X className="h-3 w-3 mr-1" />
                    Limpar
                  </Button>
                )}
              </div>
            </SheetHeader>
            <ScrollArea className="flex-1 p-4">
              <div className="space-y-4">
                {/* v5.0.0: Responsável (assigned_to) */}
                <div className="space-y-1.5">
                  <Label className="text-xs flex items-center gap-1.5">
                    <Briefcase className="h-3 w-3" />
                    Responsável
                  </Label>
                  <CrmUserFilter
                    value={assignedToFilter}
                    onChange={setAssignedToFilter}
                    className="w-full h-8 justify-start text-xs"
                  />
                </div>

                <Separator />

                {/* v6.0.0 (2026-05-16): Origem do Cadastro */}
                <div className="space-y-1.5">
                  <Label className="text-xs flex items-center gap-1.5">
                    <SlidersHorizontal className="h-3 w-3" />
                    Origem do Cadastro
                  </Label>
                  <CrmDealCreationOriginFilter
                    value={creationOriginFilter}
                    onChange={setCreationOriginFilter}
                  />
                </div>

                <Separator />

                {/* v5.0.0: Data Previsão */}
                <DateFilterRow
                  label="Data Previsão"
                  fromDate={expectedCloseFrom}
                  toDate={expectedCloseTo}
                  onFromChange={setExpectedCloseFrom}
                  onToChange={setExpectedCloseTo}
                />

                {/* Data Criação */}
                <DateFilterRow
                  label="Data Criação"
                  fromDate={createdFrom}
                  toDate={createdTo}
                  onFromChange={setCreatedFrom}
                  onToChange={setCreatedTo}
                />

                {/* Data Alteração Fase */}
                <DateFilterRow
                  label="Data Alt. Fase"
                  fromDate={phaseUpdatedFrom}
                  toDate={phaseUpdatedTo}
                  onFromChange={setPhaseUpdatedFrom}
                  onToChange={setPhaseUpdatedTo}
                />

                {/* Data Ganho */}
                <DateFilterRow
                  label="Data Ganho"
                  fromDate={wonDateFrom}
                  toDate={wonDateTo}
                  onFromChange={setWonDateFrom}
                  onToChange={setWonDateTo}
                />
                

                {/* Data Perdido */}
                <DateFilterRow
                  label="Data Perdido"
                  fromDate={lostDateFrom}
                  toDate={lostDateTo}
                  onFromChange={setLostDateFrom}
                  onToChange={setLostDateTo}
                />

                <Separator />

                {/* v7.0.0 (2026-07-11): Motivos de Perda (multi-seleção) */}
                <div className="space-y-1.5">
                  <Label className="text-xs flex items-center gap-1.5">
                    <X className="h-3 w-3" />
                    Motivos de Perda
                  </Label>
                  <CrmLossReasonFilter
                    value={lossReasonIds}
                    onChange={setLossReasonIds}
                    className="w-full h-8 justify-start text-xs"
                  />
                </div>
              </div>
            </ScrollArea>
          </SheetContent>
        </Sheet>

        {/* Botão Limpar Filtros */}
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearFilters}
            className="h-8 px-2 text-xs text-muted-foreground hover:text-foreground"
          >
            <X className="h-3 w-3 mr-1" />
            Limpar
          </Button>
        )}




        {/* Indicador de carregamento */}
        {isFetching && !isLoading && (
          <span className="text-xs text-muted-foreground animate-pulse">
            Atualizando...
          </span>
        )}
      </div>

      {/* Tabela Premium — com setinhas de scroll horizontal (ScrollIndicators) */}
      <ScrollIndicators className="flex-1">
        <DealsListTableProvider value={listTableCtx}>
        <div className="h-full w-full overflow-hidden overflow-y-auto">
        {viewMode === 'grouped' ? (
          /* v8.0.0: visualização agrupada (reaproveita filtros, RPC e modais) */
          <DealGroupedView
            baseFilters={dealFilters}
            groupBy={groupBy}
            sortField={sortField}
            sortDirection={sortDirection}
            defaultExpanded={groupsDefault === 'expanded'}
            expandAllSignal={expandAllSignal}
            expandAllValue={groupsDefault === 'expanded'}
          />
        ) : isLoading ? (

          <div className="space-y-2 p-4">
            {[1, 2, 3, 4, 5].map(i => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : deals.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 gap-3">
            <Briefcase className="h-12 w-12 text-muted-foreground/40" />
            <div className="text-center">
              <h3 className="font-medium text-sm">Nenhum negócio encontrado</h3>
              <p className="text-xs text-muted-foreground mt-1">
                {hasActiveFilters
                  ? 'Tente ajustar os filtros de busca'
                  : 'Crie seu primeiro negócio para começar'}
              </p>
            </div>
            {!hasActiveFilters && (
              <Button 
                size="xs"
                onClick={() => setFormOpen(true)}
                className="hover:shadow-md hover:scale-[1.02] transition-all duration-200"
              >
                <Plus className="mr-1.5" />
                Novo Negócio
              </Button>
            )}
          </div>
        ) : (
          /* v8.0.0: mesma tabela usada pelos grupos (DealsListTable) */
          <DealsListTable deals={deals} />
        )}
        </div>
        </DealsListTableProvider>
      </ScrollIndicators>


      {/* Footer de Paginação (v2.1.0) — na visão agrupada cada grupo tem a sua */}
      {viewMode === 'list' && !isLoading && deals.length > 0 && (

        <div className="border-t px-4 py-2 bg-background">
          <PaginationFooter
            totalItems={totalCount}
            currentPage={currentPage}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            pageSizeOptions={[10, 20, 50, 100]}
            borderClassName=""
            className="mt-0 pt-0"
          />
        </div>
      )}

      {/* Dialog de formulário */}
      <DealFormDialog
        open={formOpen}
        onOpenChange={handleCloseForm}
        deal={editingDeal}
        /* v3.5.0: o modal de sucesso agora vive dentro do DealFormDialog;
           aqui só informamos como abrir o negócio (modal premium). */
        onViewDeal={(dealId) => {
          setViewDealId(dealId);
          setViewModalOpen(true);
        }}
        onSuccess={() => {
          // Atualiza a listagem para refletir o novo registro
          queryClient.invalidateQueries({ queryKey: ['crm-deals-paginated'] });
        }}
      />




      {/* Modal Premium de Detalhes */}
      <DealDetailsModalPremium
        dealId={viewDealId}
        open={viewModalOpen}
        onOpenChange={setViewModalOpen}
        onEdit={(deal) => {
          setViewModalOpen(false);
          // Converte CrmDeal para formato esperado
          setEditingDeal(deal);
          setFormOpen(true);
        }}
        onDeleted={() => {
          queryClient.invalidateQueries({ queryKey: ['crm-deals-paginated'] });
        }}
        onOpenConversation={(conversationId) => {
          console.log('Abrir conversa:', conversationId);
        }}
      />

      {/* Modal de Transferência em Massa */}
      <BulkTransferDialog
        open={bulkTransferOpen}
        onOpenChange={setBulkTransferOpen}
        context="deals"
        showDealOptions={true}
      />
    </div>
  );
}
